import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Typography, Box, Container, Link, Avatar, Paper, Badge, Stack, useTheme, decomposeColor } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouteLink, Route, Routes, useNavigate, useParams } from 'react-router';
import { MainContext } from '../../context';
import { createRoute, formatCompactNumber } from '../../utils';
import { DelayedLoadIndicator } from '../../components/delayed-load-indicator';
import { ExtensionIcon } from '../../components/extension/extension-icon';
import { HoverPopover } from '../../components/hover-popover';
import { isNotFound } from '../../server-request';
import { TextDivider } from '../../components/text-divider';
import { ExtensionRatingStars } from './extension-rating-stars';
import { NamespaceDetailRoutes } from '../namespace-detail/namespace-detail-routes';
import { ExtensionDetailOverview } from './extension-detail-overview';
import { ExtensionDetailChanges } from './extension-detail-changes';
import { ExtensionDetailReviews } from './extension-detail-reviews';
import { ExtensionDetailRoutes } from './extension-detail-routes';
import { TrustedPublishingIcon } from './trusted-publishing-icon';
import { useExtensionDetail } from './use-extension-details';
import { KbdKey } from '../../components/kbd-key';
import { useShortcut } from '../../hooks/use-shortcut';
import { NAVBAR_HEIGHT, NAVBAR_HEIGHT_PX } from '../../default/theme';
import { useSetExtensionTint } from '../../context/navbar-chrome-context';
import { PageContainer } from '../../components/page-container';
import { PillTab, PillTabs } from '../../components/pill-tabs';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import WarningIcon from '@mui/icons-material/Warning';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
const inlineLinkStyle = {
    display: 'contents',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' }
};
const StyledRouteLink = styled(RouteLink)(inlineLinkStyle);
const StyledLink = styled(Link)(inlineLinkStyle);
const StyledHoverPopover = styled(HoverPopover)({ display: 'flex', alignItems: 'center' });
const PreviewBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': { top: theme.spacing(1), right: theme.spacing(-5) }
}));
var ExtensionTab;
(function (ExtensionTab) {
    ExtensionTab["OVERVIEW"] = "overview";
    ExtensionTab["CHANGES"] = "changes";
    ExtensionTab["REVIEWS"] = "reviews";
})(ExtensionTab || (ExtensionTab = {}));
const TAB_VALUES = new Set(Object.values(ExtensionTab));
const isTabSegment = (segment) => TAB_VALUES.has(segment ?? '');
const parseTab = (segment) => (isTabSegment(segment) ? segment : ExtensionTab.OVERVIEW);
const buildExtensionPath = (namespace, name, target, ...extra) => {
    const arr = [ExtensionDetailRoutes.ROOT, namespace, name];
    if (target)
        arr.push(target);
    arr.push(...extra);
    return createRoute(arr);
};
const UnverifiedBanner = ({ extension, headerTextColor, themeType }) => {
    const { pageSettings } = useContext(MainContext);
    if (extension.verified)
        return null;
    return (_jsxs(Paper, { sx: {
            display: 'flex',
            maxWidth: '800px',
            p: 2,
            mt: 0,
            mx: { xs: 0, md: 6 },
            mb: { xs: 2, md: 4 },
            bgcolor: `warning.${themeType}`,
            color: headerTextColor,
            '& a': { color: headerTextColor, textDecoration: 'underline' }
        }, children: [_jsx(WarningIcon, { fontSize: 'large' }), _jsxs(Box, { ml: 1, children: ["This version of the \u201C", extension.displayName ?? extension.name, "\u201D extension was published by", ' ', _jsx(Link, { href: extension.publishedBy.homepage, children: extension.publishedBy.loginName }), ". That user account is not a verified publisher of the namespace \u201C", extension.namespace, "\u201D of this extension.", ' ', _jsx(Link, { href: pageSettings.urls.namespaceAccessInfo, target: '_blank', children: "See the documentation" }), ' ', "to learn how we handle namespaces and what you can do to eliminate this warning."] })] }));
};
const VerificationIcon = ({ verified, color }) => {
    const { pageSettings } = useContext(MainContext);
    const icon = verified ? _jsx(VerifiedUserIcon, { fontSize: 'small' }) : _jsx(WarningIcon, { fontSize: 'small' });
    const title = verified ? 'Verified publisher' : 'Unverified publisher';
    return (_jsx(StyledLink, { href: pageSettings.urls.namespaceAccessInfo, target: '_blank', title: title, sx: { color }, children: icon }));
};
const UserPopover = ({ user, color }) => {
    const popupContent = (_jsxs(Box, { display: 'flex', flexDirection: 'row', children: [user.avatarUrl && (_jsx(Avatar, { src: user.avatarUrl, alt: user.fullName ?? user.loginName, variant: 'rounded', sx: { width: '60px', height: '60px' } })), _jsxs(Box, { ml: 2, children: [user.fullName && _jsx(Typography, { variant: 'h6', children: user.fullName }), _jsx(Typography, { variant: 'body1', children: user.loginName })] })] }));
    return (_jsx(StyledHoverPopover, { id: `user_${user.loginName}_popover`, popupContent: popupContent, children: _jsx(StyledLink, { href: user.homepage, sx: { color }, children: user.avatarUrl ? (_jsxs(_Fragment, { children: [user.loginName, "\u00A0", _jsx(Avatar, { src: user.avatarUrl, alt: user.loginName, sx: { width: '20px', height: '20px' } })] })) : (user.loginName) }) }));
};
const LicenseLink = ({ extension, color }) => {
    if (extension.files.license) {
        return (_jsx(StyledLink, { href: extension.files.license, sx: { color }, title: extension.license ? 'License type' : undefined, children: extension.license || 'Provided license' }));
    }
    return _jsx(_Fragment, { children: extension.license || 'Unlicensed' });
};
export const ExtensionHeaderInfo = ({ extension, headerTextColor }) => {
    const downloadCountFormatted = formatCompactNumber(extension.downloadCount || 0);
    const reviewCountFormatted = formatCompactNumber(extension.reviewCount || 0);
    return (_jsxs(Box, { sx: { pt: 0.5, overflow: 'visible', flex: 1, minWidth: 0 }, children: [_jsx(PreviewBadge, { color: 'secondary', badgeContent: 'Preview', invisible: !extension.preview, children: _jsx(Typography, { component: 'h1', sx: {
                        fontSize: { xs: '1.7rem', md: '1.938rem' },
                        fontWeight: 800,
                        letterSpacing: '-0.025em',
                        mb: 1,
                        color: headerTextColor
                    }, children: extension.displayName ?? extension.name }) }), extension.deprecated && (_jsxs(Stack, { direction: 'row', alignItems: 'center', children: [_jsx(WarningIcon, { fontSize: 'small' }), _jsxs(Typography, { children: ["This extension has been deprecated.", extension.replacement && (_jsxs(_Fragment, { children: ["\u00A0Use", ' ', _jsx(StyledLink, { sx: { color: headerTextColor }, href: extension.replacement.url, children: extension.replacement.displayName }), ' ', "instead."] }))] })] })), _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    color: headerTextColor,
                    flexDirection: { xs: 'column', md: 'row' }
                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', flexShrink: 0 }, children: [_jsx(VerificationIcon, { verified: extension.verified, color: headerTextColor }), "\u00A0", _jsx(StyledRouteLink, { to: createRoute([NamespaceDetailRoutes.ROOT, extension.namespace]), style: { color: headerTextColor }, children: extension.namespaceDisplayName })] }), _jsx(TextDivider, { backgroundColor: headerTextColor, collapseSmall: true }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', flexShrink: 0 }, children: ["Published by\u00A0", _jsx(UserPopover, { user: extension.publishedBy, color: headerTextColor }), extension.publishedWithTrustedPublishing && (_jsxs(_Fragment, { children: [_jsx(TextDivider, { backgroundColor: headerTextColor, collapseSmall: true }), _jsx(TrustedPublishingIcon, { color: headerTextColor })] }))] }), _jsx(TextDivider, { backgroundColor: headerTextColor, collapseSmall: true }), _jsx(Box, { sx: { display: 'flex', alignItems: 'center' }, children: _jsx(LicenseLink, { extension: extension, color: headerTextColor }) })] }), _jsx(Box, { mt: 2, mb: 2, overflow: 'auto', children: _jsx(Typography, { sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: extension.description }) }), _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    color: headerTextColor,
                    justifyContent: { xs: 'center', md: 'flex-start' }
                }, children: [_jsxs(Box, { component: 'span', sx: { display: 'flex', alignItems: 'center' }, title: extension.downloadCount && extension.downloadCount >= 1000
                            ? `${extension.downloadCount} downloads`
                            : undefined, children: [_jsx(SaveAltIcon, { fontSize: 'small' }), "\u00A0", downloadCountFormatted, "\u00A0", extension.downloadCount === 1 ? 'download' : 'downloads'] }), _jsx(TextDivider, { backgroundColor: headerTextColor }), _jsxs(StyledLink, { href: createRoute([ExtensionDetailRoutes.ROOT, extension.namespace, extension.name, 'reviews']), sx: { display: 'flex', alignItems: 'center', color: headerTextColor }, title: extension.averageRating === undefined
                            ? 'Not rated yet'
                            : `Average rating: ${Math.round(extension.averageRating * 10) / 10} out of 5 (${extension.reviewCount} reviews)`, children: [_jsx(ExtensionRatingStars, { number: extension.averageRating ?? 0, fontSize: 'small' }), "(", reviewCountFormatted, ")"] })] })] }));
};
const loadErrorMessage = (error, namespace, name) => {
    if (isNotFound(error)) {
        return `Extension Not Found: ${namespace}.${name}`;
    }
    return error instanceof Error ? error.message : 'Failed to load extension details';
};
// The luma CSS grayscale() resolves a color to; deprecated bands paint through that filter.
const grayscaleOf = (color) => {
    const [r, g, b] = decomposeColor(color).values;
    const y = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
    return `rgb(${y}, ${y}, ${y})`;
};
// Declares the header band as the nav's tint region: its gallery color (null
// for default bands) and how deep it runs. The nav does the switching; the
// resize observer keeps the depth fresh as the band's content reflows.
const useNavTintFromBand = (color, bandRef) => {
    const setTint = useSetExtensionTint();
    useEffect(() => {
        const band = bandRef.current;
        if (!band)
            return;
        // Fires once on observe, then on size changes.
        const observer = new ResizeObserver(() => setTint({ color, depth: band.getBoundingClientRect().bottom + window.scrollY }));
        observer.observe(band);
        return () => {
            observer.disconnect();
            setTint(null);
        };
    }, [color, bandRef, setTint]);
};
const ExtensionHeader = ({ extension, bandRef }) => {
    const theme = useTheme();
    const { pageSettings } = useContext(MainContext);
    const themeType = (extension.galleryTheme || pageSettings.themeType) ?? 'light';
    const fallbackColor = theme.palette.neutral[themeType];
    let headerColor = extension.galleryColor || fallbackColor;
    try {
        decomposeColor(headerColor);
    }
    catch {
        headerColor = fallbackColor;
    }
    const headerTextColor = theme.palette.getContrastText(headerColor);
    const usesDefaultBg = !extension.galleryColor;
    // Only real gallery colors tint the nav; over default bands it keeps its
    // theme glass, which is visually flush with bg2 already.
    useNavTintFromBand(usesDefaultBg ? null : extension.deprecated ? grayscaleOf(headerColor) : headerColor, bandRef);
    return (_jsx(Box, { ref: bandRef, sx: {
            bgcolor: usesDefaultBg ? 'bg2' : headerColor,
            color: usesDefaultBg ? 'text.primary' : headerTextColor,
            // Extend the band under the sticky nav so its glass picks up the header color.
            mt: `-${NAVBAR_HEIGHT}`,
            pt: NAVBAR_HEIGHT,
            borderBottom: '1px solid',
            borderColor: 'divider',
            filter: extension.deprecated ? 'grayscale(100%)' : undefined
        }, children: _jsx(Container, { maxWidth: 'xl', children: _jsxs(Box, { sx: { pt: '1.125rem', pb: '1.75rem' }, children: [_jsx(UnverifiedBanner, { extension: extension, headerTextColor: usesDefaultBg ? theme.palette.text.primary : headerTextColor, themeType: themeType }), _jsxs(Box, { sx: {
                            display: 'flex',
                            width: '100%',
                            flexDirection: { xs: 'column', md: 'row' },
                            textAlign: { xs: 'center', md: 'start' },
                            alignItems: { xs: 'center', md: 'flex-start' },
                            gap: { xs: 2, md: '1.625rem' }
                        }, children: [_jsx(ExtensionIcon, { extension: extension, sx: { width: '96px', height: '96px', flexShrink: 0, objectFit: 'contain' } }), _jsx(ExtensionHeaderInfo, { extension: extension, headerTextColor: usesDefaultBg ? theme.palette.text.primary : headerTextColor })] })] }) }) }));
};
export const ExtensionDetail = () => {
    const { namespace, name, target, '*': splat } = useParams();
    const navigate = useNavigate();
    const { pageSettings } = useContext(MainContext);
    const version = splat || undefined;
    const effectiveVersion = isTabSegment(version) ? undefined : version;
    const activeTab = parseTab(version);
    // Tab switches preserve scroll (see ScrollRestoration); when scrolled deep, glide
    // up so the new panel starts under the pinned pills.
    const bandRef = useRef(null);
    const prevTab = useRef(activeTab);
    useEffect(() => {
        if (prevTab.current === activeTab)
            return;
        prevTab.current = activeTab;
        const band = bandRef.current;
        if (!band)
            return;
        const pin = band.getBoundingClientRect().bottom + window.scrollY - NAVBAR_HEIGHT_PX;
        // Rest above the pin point
        const target = Math.max(0, pin);
        if (window.scrollY > target) {
            window.scrollTo({ top: target });
        }
    }, [activeTab]);
    // React Router v6 returns a possibly undefined type for params, but our route configuration guarantees these will be defined.
    const { data: extension, error: extensionError, isLoading: extensionLoading } = useExtensionDetail(namespace, name, target, effectiveVersion);
    // Tab switches keep the scroll position (the effect above adjusts it).
    const goToTab = useCallback((path) => navigate(path, { state: { preserveScroll: true } }), [navigate]);
    const navigateToVersion = useCallback((selectedVersion) => {
        if (!namespace || !name)
            return;
        navigate(selectedVersion === 'latest'
            ? buildExtensionPath(namespace, name, target)
            : buildExtensionPath(namespace, name, target, selectedVersion));
    }, [navigate, namespace, name, target]);
    // Computed before the early return so the shortcut hooks below run unconditionally.
    const basePath = namespace && name ? buildExtensionPath(namespace, name, target) : '';
    const changesPath = namespace && name ? buildExtensionPath(namespace, name, target, ExtensionTab.CHANGES) : '';
    const reviewsPath = namespace && name ? buildExtensionPath(namespace, name, target, ExtensionTab.REVIEWS) : '';
    useShortcut({
        key: 'o',
        label: 'Extension overview',
        order: 40,
        callback: () => goToTab(basePath),
        enabled: !!basePath
    });
    useShortcut({
        key: 'c',
        label: 'Extension changelog',
        order: 50,
        callback: () => goToTab(changesPath),
        enabled: !!basePath
    });
    useShortcut({
        key: 'r',
        label: 'Extension reviews',
        order: 60,
        callback: () => goToTab(reviewsPath),
        enabled: !!basePath
    });
    if (!namespace || !name)
        return null;
    let overviewPath = basePath;
    if (version && !isTabSegment(version)) {
        overviewPath = buildExtensionPath(namespace, name, target, version);
    }
    else if (extension && !extension.versionAlias.includes('latest')) {
        overviewPath = buildExtensionPath(namespace, name, target, extension.version);
    }
    const HeadTags = pageSettings.elements.extensionHeadTags;
    return (_jsxs(_Fragment, { children: [HeadTags && _jsx(HeadTags, { extension: extension, pageSettings: pageSettings }), _jsx(DelayedLoadIndicator, { loading: extensionLoading }), extension && (_jsxs(_Fragment, { children: [_jsx(ExtensionHeader, { extension: extension, bandRef: bandRef }), _jsxs(PageContainer, { flushTop: true, children: [_jsxs(PillTabs, { value: activeTab, gutters: { xs: '1rem', sm: '1.5rem', md: '0.375rem' }, children: [_jsx(PillTab, { value: ExtensionTab.OVERVIEW, label: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '0.4375rem' }, children: ["Overview", _jsx(KbdKey, { children: "o" })] }), component: RouteLink, to: overviewPath, state: { preserveScroll: true } }), _jsx(PillTab, { value: ExtensionTab.CHANGES, label: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '0.4375rem' }, children: ["Changes", _jsx(KbdKey, { children: "c" })] }), component: RouteLink, to: changesPath, state: { preserveScroll: true } }), _jsx(PillTab, { value: ExtensionTab.REVIEWS, label: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '0.4375rem' }, children: ["Ratings & Reviews", _jsx(KbdKey, { children: "r" })] }), component: RouteLink, to: reviewsPath, state: { preserveScroll: true } })] }), _jsx(Box, { sx: { pt: 2 }, children: _jsxs(Routes, { children: [_jsx(Route, { path: ExtensionTab.REVIEWS, element: _jsx(ExtensionDetailReviews, { extension: extension }) }), _jsx(Route, { path: ExtensionTab.CHANGES, element: _jsx(ExtensionDetailChanges, { extension: extension }) }), _jsx(Route, { path: '*', element: _jsx(ExtensionDetailOverview, { extension: extension, selectVersion: navigateToVersion }) })] }) })] })] })), extensionError && (_jsx(Box, { p: 4, children: _jsx(Typography, { variant: 'h5', children: loadErrorMessage(extensionError, namespace, name) }) }))] }));
};
//# sourceMappingURL=extension-detail.js.map